import { createHttpClient } from '@adapters/HttpClient';
import type { CurrentUserResponse } from '@modules/Auth/models/Auth';
import { apiClientWH } from '@shared/services/ApiClient';
import { useAuthStore } from '@shared/stores/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationsApiAdapter } from '../adapters/NotificationsApiAdapter';
import type { NotificationPreferenceChoice } from '../models/Notification.types';

export function useNotificationOnboarding() {
  const userId = useAuthStore((state) => state.user?.id);
  const scope = useAuthStore((state) => state.getAuthorizationScope());
  const queryClient = useQueryClient();
  const adapter = new NotificationsApiAdapter(createHttpClient(apiClientWH));
  const query = useQuery({
    queryKey: ['notification-preferences', scope, 'onboarding'],
    queryFn: ({ signal }) => adapter.getAllPreferences(signal),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const completion = useMutation({
    mutationFn: (preferences: NotificationPreferenceChoice[]) => adapter.completeOnboarding(preferences),
    onSuccess: async () => {
      if (useAuthStore.getState().getAuthorizationScope() !== scope) return;
      await queryClient.cancelQueries({ queryKey: ['me'], exact: true });
      const current = useAuthStore.getState();
      if (current.getAuthorizationScope() !== scope || !current.user) return;
      current.setUser({ ...current.user, notification_preferences_configured: true });
      queryClient.setQueryData<CurrentUserResponse>(['me'], (profile) =>
        profile
          ? {
              ...profile,
              user: { ...profile.user, notification_preferences_configured: true },
            }
          : profile,
      );
      void queryClient.invalidateQueries({ queryKey: ['notification-preferences'], refetchType: 'none' });
    },
  });

  return {
    preferences: (query.data ?? []).filter(
      (preference) => preference.user_id === userId && preference.notification_type?.is_active,
    ),
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    completion,
  };
}
